import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { cardBackground, cardBorder } from '../styles/themeHelpers';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resendVerificationEmail } from '../services/authService';
import PasswordField from '../components/common/PasswordField';
import AnimatedBackground from '../components/common/AnimatedBackground';


const Background = styled.div`
  background: ${({ theme }) =>
    theme.profile?.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients?.background || theme.colors.background};
  position: relative;
  
  /* Desktop - fixed height, no scroll */
  @media (min-width: 769px) {
    height: 100vh;
    overflow: hidden;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
  }
  
  /* Mobile - scrollable */
  @media (max-width: 768px) {
    min-height: 100vh;
    overflow: visible;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: ${({ theme }) =>
      theme.profile?.pageBackground === 'transparent'
        ? 'none'
        : "url('data:image/svg+xml,%3Csvg width=\\'2\\' height=\\'2\\' xmlns=\\'http://www.w3.org/2000/svg\\'%3E%3Ccircle cx=\\'1\\' cy=\\'1\\' r=\\'0.2\\' fill=\\'#003040\\' fill-opacity=\\'0.3\\'/%3E%3C/svg%3E')"};
    pointer-events: none;
  }
`;


const CenterBlock = styled.div`
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  /* Desktop - centered, fixed position */
  @media (min-width: 769px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    justify-content: center;
    padding: 15px;
    overflow: hidden;
  }
  
  /* Mobile - scrollable from top */
  @media (max-width: 768px) {
    position: relative;
    justify-content: flex-start;
    min-height: 100vh;
    padding: 80px 15px 20px; /* Top padding to avoid header */
  }
`;

const AccessFrame = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 2px solid ${({ theme }) => cardBorder(theme)};
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.3);
  padding: 30px;
  border-radius: 12px;
  max-width: 480px;
  width: 100%;
  position: relative;
  text-align: center;
  z-index: 10;
  backdrop-filter: none;
  flex-shrink: 0;

  @media (max-width: 768px) {
    max-width: 450px;
    padding: 25px;
  }
`;

const AccessTitle = styled.h1`
  font-size: 1.7rem;
  color: #00d4ff;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 30px;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
`;


const InputBlock = styled.div`
  margin-bottom: 10px;
`;

const InputLabel = styled.div`
  color: #00d4ff;
  font-size: 13px;
  margin-bottom: 5px;
  letter-spacing: 1px;
  text-align: left;
  font-weight: 500;
`;

const InputField = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  padding: 10px 12px;
  margin-bottom: 0;
  outline: none;
  text-align: left;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:focus {
    border-color: #00d4ff;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const StatusText = styled.div`
  color: #b0b0b0;
  font-size: 12px;
  margin-bottom: 8px;
  letter-spacing: 1px;
  text-align: left;
`;

const AuthorizeButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 12px 0;
  margin: 15px 0;
  cursor: pointer;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #00a8cc, #007799);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LinkRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 6px;
    align-items: center;
  }
`;

const FrameLink = styled(Link)`
  color: #00d4ff;
  font-size: 12px;
  text-decoration: none;
  letter-spacing: 1px;
  padding: 8px 12px;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    font-size: 10px;
    padding: 6px 10px;
    letter-spacing: 0.5px;
  }

  &:hover {
    color: #fff;
    border-color: #00d4ff;
    background: rgba(0, 212, 255, 0.1);
  }
`;

const ErrorMsg = styled.div`
  color: #ff3333;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  text-align: center;
  margin-bottom: 10px;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: #00d4ff;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  margin-top: 8px;
  transition: color 0.3s ease;

  &:hover {
    color: #fff;
  }
`;

const InputHint = styled.span`
  color: #b0b0b0;
  font-size: 12px;
  letter-spacing: 1px;
  text-align: left;
  display: block;
  margin-top: 4px;
`;

const LoginPage: React.FC = () => {
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState<boolean>(false);
  const [localError, setLocalError] = useState('');
  const [resendStatus, setResendStatus] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const navigate = useNavigate();

  // Отслеживаем изменения пользователя после успешного входа
  useEffect(() => {
    if (loginSuccess && user) {
      switch (user.role) {
        case 'Admin':
          navigate('/admin');
          break;
        case 'Referee':
          navigate('/referee/profile');
          break;
        // Field login is handled by FieldContext, not AuthContext
        default: // Customer
          navigate('/');
          break;
      }
    }
  }, [loginSuccess, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');
    setResendStatus('');
    setLoading(true);

    try {
      await login(email, password);
      setLoginSuccess(true);
    } catch (err: any) {
      setLocalError(err.message || 'Access denied');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendStatus('');
    try {
      const success = await resendVerificationEmail(email);
      if (success) {
        setResendStatus('Verification email has been resent. Please check your inbox.');
      } else {
        setResendStatus('Failed to resend email. Please try again later.');
      }
    } catch (err) {
      setResendStatus('An error occurred. Please try again later.');
    }
  };


  // Check if error mentions unverified email
  const isEmailVerificationError = localError.toLowerCase().includes('email') && 
                                  localError.toLowerCase().includes('not verified');

  return (
    <Background>
      <CenterBlock>
        <AccessFrame>
          <AccessTitle>LOGIN</AccessTitle>
          <form onSubmit={handleSubmit}>
            {localError && 
              <ErrorMsg>
                {localError}
                {isEmailVerificationError && (
                  <div>
                    <ResendButton 
                      type="button"
                      onClick={handleResendVerification}
                    >
                      Resend Verification Email
                    </ResendButton>
                  </div>
                )}
              </ErrorMsg>
            }
            {resendStatus && <StatusText>{resendStatus}</StatusText>}
            <InputBlock>
              <InputLabel>Email Address</InputLabel>
              <InputField
                type="email"
                name="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                autoComplete="email"
                autoFocus
                required
                placeholder="Enter your email"
              />
              <InputHint>Format: user@domain.com</InputHint>
            </InputBlock>
            <InputBlock>
              <PasswordField
                label="Password"
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                emailForChecks={email}
                showStrength={false}
              />
            </InputBlock>
            <AuthorizeButton 
              type="submit" 
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </AuthorizeButton>
          </form>
          <LinkRow>
            <FrameLink to="/register">Create Account</FrameLink>
            <FrameLink to="/forgot-password">Forgot Password</FrameLink>
            <FrameLink to="/">← Back to Home</FrameLink>
          </LinkRow>
        </AccessFrame>
      </CenterBlock>
    </Background>
  );
};

export default LoginPage; 
